import { Link } from 'react-router-dom';
import {
  Home, Compass, Film, MessageCircle, Users, Gamepad2, Store, Calendar,
  PlusSquare, Sparkles, MapPin, BadgeCheck, Coins, Mic, Heart, MessageSquare,
  Play, ChevronRight, Lock, Flame, Bot,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';

/* =============================================================
 * DEMO / DISPLAY DATA — ISOLATED.  Replace with live data later.
 * Nothing below talks to a backend; it only establishes the
 * approved concept layout. Keep this block self-contained.
 * ============================================================= */
export const DEMO = {
  categories: [
    { key: 'habitat', label: 'My Habitat', icon: Home },
    { key: 'familiar', label: 'Familiar', icon: Sparkles },
    { key: 'adventures', label: 'Adventures', icon: Compass },
    { key: 'collections', label: 'Collections', icon: Store },
    { key: 'moments', label: 'Moments', icon: Film },
  ],
  friends: [
    { name: 'Bjorn Ironside', realm: 'Thornhaven', online: true },
    { name: 'Freydis', realm: 'Thornhaven', online: true },
    { name: 'Ragnar L.', realm: 'Midgard', online: true },
    { name: 'Astrid Vale', realm: 'Midgard', online: false },
    { name: 'Ulf the Bold', realm: 'Thornhaven', online: false },
  ],
  voice: { room: 'The Hearth Circle', topic: 'Tales of the Nine Realms', listeners: 42, speakers: 6 },
  creators: [
    { name: 'Saga Weaver', handle: 'sagaweaver', supporters: 1280 },
    { name: 'Rune Smith', handle: 'runesmith', supporters: 864 },
  ],
  reels: [
    { id: 'r1', creator: 'Freydis', caption: 'Bifröst at dusk', likes: '12.4k', comments: 318, views: '201k' },
    { id: 'r2', creator: 'Bjorn', caption: 'Forging a seax', likes: '8.1k', comments: 142, views: '96k' },
  ],
};

/* Named asset slot — ready for direct Hailuo replacement via `src`. */
export const AssetSlot = ({ name, src, className = '', rounded = 'rounded-2xl', label, children }) => (
  <div
    data-asset-slot={name}
    className={`relative overflow-hidden ${rounded} ${!src ? 'mg-slot-grad' : ''} ${className}`}
    style={src ? { backgroundImage: `url(${src})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
  >
    {!src && label && (
      <span className="absolute left-2 bottom-2 text-[9px] uppercase tracking-widest text-mg-purple/50 font-semibold">{label}</span>
    )}
    {children}
  </div>
);

const SectionTitle = ({ children, to }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="mg-heading text-sm text-foreground">{children}</h3>
    {to && <Link to={to} className="text-xs text-mg-purple hover:underline flex items-center gap-0.5">See all <ChevronRight className="w-3 h-3" /></Link>}
  </div>
);

const ComingSoon = ({ className = '' }) => (
  <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-mg-purple/15 text-mg-purple border border-mg-rune/30 ${className}`}>Coming Soon</span>
);

/* ---- Profile identity header (desktop) ---- */
export const ProfileIdentityHeader = ({ user }) => (
  <div className="mg-card-elevated overflow-hidden" data-testid="profile-identity-header">
    <AssetSlot name="character-cover" rounded="" className="h-44 w-full" label="character cover">
      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
    </AssetSlot>
    <div className="px-6 pb-5 -mt-12 relative">
      <div className="flex items-end justify-between gap-4">
        <div className="flex items-end gap-4">
          <Avatar className="w-24 h-24 border-4 border-background mg-glow">
            <AvatarImage src={user?.avatar} alt={user?.display_name} />
            <AvatarFallback className="text-2xl">{user?.display_name?.[0] || 'M'}</AvatarFallback>
          </Avatar>
          <div className="pb-1">
            <div className="flex items-center gap-1.5">
              <h2 className="font-heading text-2xl font-bold">{user?.display_name || 'Traveler'}</h2>
              <BadgeCheck className="w-5 h-5 text-mg-purple" />
            </div>
            <p className="text-sm text-muted-foreground">@{user?.username || 'traveler'}</p>
            <span className="inline-flex items-center gap-1 mt-1.5 text-xs text-mg-purple bg-mg-purple/10 border border-mg-rune/30 rounded-full px-2 py-0.5">
              <MapPin className="w-3 h-3" /> Thornhaven · Midgard
            </span>
          </div>
        </div>
        <Link to={`/profile/${user?.username}`} className="mg-btn-secondary text-sm" data-testid="view-profile-btn">View Profile</Link>
      </div>
      <p className="text-sm text-foreground/80 mt-3 max-w-xl">
        {user?.bio || 'Wandering the Nine Realms, forging tales and raising a habitat in Thornhaven.'}
      </p>
      <div className="flex gap-6 mt-3 text-sm">
        <span><b className="text-foreground">{user?.posts_count ?? user?.post_count ?? 0}</b> <span className="text-muted-foreground">Posts</span></span>
        <span><b className="text-foreground">{user?.followers_count ?? 0}</b> <span className="text-muted-foreground">Followers</span></span>
        <span><b className="text-foreground">{user?.following_count ?? 0}</b> <span className="text-muted-foreground">Following</span></span>
      </div>
      <div className="flex gap-4 mt-5 overflow-x-auto scrollbar-hide">
        {DEMO.categories.map((c) => (
          <button key={c.key} className="flex flex-col items-center gap-1.5 group flex-none" data-testid={`category-${c.key}`}>
            <span className="w-14 h-14 rounded-full mg-card-elevated flex items-center justify-center group-hover:border-mg-rune transition-colors">
              <c.icon className="w-5 h-5 text-mg-purple" />
            </span>
            <span className="text-[11px] text-muted-foreground group-hover:text-foreground">{c.label}</span>
          </button>
        ))}
      </div>
    </div>
  </div>
);

/* ---- My Habitat panel ---- */
export const HabitatPanel = ({ idSuffix = '' }) => (
  <div className="mg-card-elevated p-4" data-testid={`habitat-panel${idSuffix}`}>
    <SectionTitle>My Habitat</SectionTitle>
    <AssetSlot name="profile-habitat" className="h-40 w-full" label="habitat scene">
      <span className="absolute top-2 right-2"><ComingSoon /></span>
    </AssetSlot>
    <div className="flex items-center justify-between mt-3">
      <div>
        <p className="font-semibold text-sm">Thornhaven Longhouse</p>
        <p className="text-xs text-muted-foreground">Personal habitat · Midgard</p>
      </div>
      <button className="mg-btn-primary text-sm" data-testid="visit-habitat-btn">Visit</button>
    </div>
  </div>
);

/* ---- Reels preview ---- */
export const ReelsPanel = () => (
  <div className="mg-card-elevated p-4" data-testid="reels-panel">
    <SectionTitle to="/reels">Sagas</SectionTitle>
    <div className="grid grid-cols-2 gap-3">
      {DEMO.reels.map((r) => (
        <Link to="/reels" key={r.id} className="relative block aspect-[9/16] group" data-testid={`reel-${r.id}`}>
          <AssetSlot name="reel-image" rounded="rounded-xl" className="absolute inset-0" label="reel" />
          <span className="absolute inset-0 flex items-center justify-center">
            <Play className="w-8 h-8 text-white/90 drop-shadow group-hover:scale-110 transition-transform" />
          </span>
          <span className="absolute left-2 bottom-2 right-2">
            <span className="block text-[11px] font-semibold text-white truncate drop-shadow">{r.caption}</span>
            <span className="flex items-center gap-2 text-[10px] text-white/80">
              <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{r.likes}</span>
              <span className="flex items-center gap-0.5"><Play className="w-3 h-3" />{r.views}</span>
            </span>
          </span>
        </Link>
      ))}
    </div>
  </div>
);

/* ---- Right rail: Friends Online + Midgard Voice + Support Creators ---- */
export const FriendsOnlinePanel = () => (
  <div className="mg-card-elevated p-4" data-testid="friends-online-panel">
    <SectionTitle to="/messages">Friends Online</SectionTitle>
    <div className="space-y-3">
      {DEMO.friends.map((f) => (
        <div key={f.name} className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="w-9 h-9"><AvatarFallback>{f.name[0]}</AvatarFallback></Avatar>
            <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background ${f.online ? 'bg-success' : 'bg-muted-foreground/40'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{f.name}</p>
            <p className="text-[11px] text-muted-foreground truncate">{f.realm}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const MidgardVoicePanel = ({ idSuffix = '' }) => (
  <div className="mg-card-elevated p-4" data-testid={`midgard-voice-panel${idSuffix}`}>
    <SectionTitle>Midgard Voice</SectionTitle>
    <div className="rounded-2xl bg-mg-purple/10 border border-mg-rune/30 p-4">
      <div className="flex items-center gap-2 text-mg-purple">
        <Mic className="w-4 h-4" />
        <span className="text-sm font-semibold">{DEMO.voice.room}</span>
      </div>
      <p className="text-xs text-muted-foreground mt-1">{DEMO.voice.topic}</p>
      <div className="flex items-center gap-3 mt-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><Flame className="w-3 h-3 text-warning" />{DEMO.voice.listeners} listening</span>
        <span>{DEMO.voice.speakers} speakers</span>
      </div>
      <button className="mg-btn-primary w-full mt-3 text-sm" data-testid="join-voice-btn">Join the Circle</button>
    </div>
  </div>
);

export const SupportCreatorsCard = ({ idSuffix = '' }) => (
  <div className="mg-card-elevated p-4" data-testid={`support-creators-panel${idSuffix}`}>
    <SectionTitle>Support Creators</SectionTitle>
    <div className="space-y-3">
      {DEMO.creators.map((c) => (
        <div key={c.handle} className="flex items-center gap-3">
          <Avatar className="w-9 h-9"><AvatarFallback>{c.name[0]}</AvatarFallback></Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{c.name}</p>
            <p className="text-[11px] text-muted-foreground">{c.supporters.toLocaleString()} supporters</p>
          </div>
          <button className="mg-btn-secondary text-xs !px-3 !py-1.5 flex items-center gap-1" data-testid={`tip-${c.handle}`}>
            <Coins className="w-3 h-3 text-warning" /> Tip
          </button>
        </div>
      ))}
    </div>
  </div>
);

export const RightRail = () => (
  <div className="space-y-5 sticky top-20">
    <FriendsOnlinePanel />
    <MidgardVoicePanel />
    <SupportCreatorsCard />
  </div>
);

/* =============================================================
 * MOBILE-NATIVE MIDGARD MODULES
 * True mobile adaptation of the canonical desktop concept —
 * same visual system, mobile-appropriate structure.
 * ============================================================= */

/* ---- Compact mobile profile / character identity ---- */
export const MobileProfileIdentity = ({ user }) => (
  <div className="mg-card-elevated overflow-hidden" data-testid="mobile-profile-identity">
    <AssetSlot name="character-cover" rounded="" className="h-24 w-full" label="character cover">
      <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/30 to-transparent" />
    </AssetSlot>
    <div className="px-4 pb-4 -mt-9 relative">
      <div className="flex items-end gap-3">
        <Avatar className="w-16 h-16 border-4 border-background mg-glow">
          <AvatarImage src={user?.avatar} alt={user?.display_name} />
          <AvatarFallback className="text-lg">{user?.display_name?.[0] || 'M'}</AvatarFallback>
        </Avatar>
        <div className="pb-1 min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <h2 className="font-heading text-lg font-bold truncate">{user?.display_name || 'Traveler'}</h2>
            <BadgeCheck className="w-4 h-4 text-mg-purple shrink-0" />
          </div>
          <p className="text-xs text-muted-foreground truncate">@{user?.username || 'traveler'}</p>
        </div>
      </div>
      <span className="inline-flex items-center gap-1 mt-2.5 text-[11px] font-medium text-mg-purple bg-mg-purple/10 border border-mg-rune/30 rounded-full px-2 py-0.5">
        <MapPin className="w-3 h-3" /> Thornhaven · Midgard
      </span>
      <div className="flex gap-5 mt-3 text-sm">
        <span><b className="text-foreground">{user?.posts_count ?? 0}</b> <span className="text-muted-foreground">Posts</span></span>
        <span><b className="text-foreground">{user?.followers_count ?? 0}</b> <span className="text-muted-foreground">Followers</span></span>
        <span><b className="text-foreground">{user?.following_count ?? 0}</b> <span className="text-muted-foreground">Following</span></span>
      </div>
      <Link to={`/profile/${user?.username}`} className="mg-btn-secondary text-sm w-full mt-3" data-testid="mobile-view-profile-btn">View Profile</Link>
      <div className="flex gap-4 mt-4 overflow-x-auto scrollbar-hide">
        {DEMO.categories.map((c) => (
          <button key={c.key} className="flex flex-col items-center gap-1.5 group flex-none" data-testid={`mobile-category-${c.key}`}>
            <span className="w-[52px] h-[52px] rounded-full mg-card flex items-center justify-center group-hover:border-mg-rune transition-colors">
              <c.icon className="w-5 h-5 text-mg-purple" />
            </span>
            <span className="text-[10px] text-muted-foreground group-hover:text-foreground">{c.label}</span>
          </button>
        ))}
      </div>
    </div>
  </div>
);

/* ---- Mobile horizontal reels strip ---- */
export const MobileReelsRow = () => (
  <div data-testid="mobile-reels-row">
    <SectionTitle to="/reels">Sagas</SectionTitle>
    <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1">
      {DEMO.reels.map((r) => (
        <Link to="/reels" key={r.id} className="relative flex-none w-28 aspect-[9/16] group" data-testid={`mobile-reel-${r.id}`}>
          <AssetSlot name="reel-image" rounded="rounded-xl" className="absolute inset-0" label="reel" />
          <span className="absolute inset-0 flex items-center justify-center">
            <Play className="w-7 h-7 text-white/90 drop-shadow group-hover:scale-110 transition-transform" />
          </span>
          <span className="absolute left-1.5 bottom-1.5 right-1.5">
            <span className="block text-[10px] font-semibold text-white truncate drop-shadow">{r.caption}</span>
            <span className="flex items-center gap-0.5 text-[9px] text-white/80"><Heart className="w-2.5 h-2.5" />{r.likes}</span>
          </span>
        </Link>
      ))}
      <Link to="/reels" className="flex-none w-28 aspect-[9/16] rounded-xl mg-card flex flex-col items-center justify-center gap-1 text-mg-purple" data-testid="mobile-reel-seeall">
        <ChevronRight className="w-5 h-5" />
        <span className="text-[10px] font-semibold">See all</span>
      </Link>
    </div>
  </div>
);

/* ---- Mobile friends online horizontal strip ---- */
export const MobileFriendsStrip = () => (
  <div data-testid="mobile-friends-strip">
    <SectionTitle to="/messages">Friends Online</SectionTitle>
    <div className="flex gap-4 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1">
      {DEMO.friends.map((f) => (
        <Link to="/messages" key={f.name} className="flex flex-col items-center gap-1.5 flex-none w-16" data-testid={`mobile-friend-${f.name.split(' ')[0].toLowerCase()}`}>
          <div className="relative">
            <Avatar className="w-12 h-12 border border-mg-rune/30"><AvatarFallback>{f.name[0]}</AvatarFallback></Avatar>
            <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background ${f.online ? 'bg-success' : 'bg-muted-foreground/40'}`} />
          </div>
          <span className="text-[10px] text-muted-foreground truncate w-full text-center">{f.name.split(' ')[0]}</span>
        </Link>
      ))}
    </div>
  </div>
);

/* ---- Mobile home composite (top of feed) ---- */
export const MobileHomeTop = ({ user }) => (
  <div className="min-[900px]:hidden space-y-5 pt-2" data-testid="mobile-home-top">
    <MobileProfileIdentity user={user} />
    <HabitatPanel idSuffix="-mobile" />
    <MobileReelsRow />
    <MobileFriendsStrip />
    <MidgardVoicePanel idSuffix="-mobile" />
    <SupportCreatorsCard idSuffix="-mobile" />
  </div>
);

/* ---- Sidebar extras: My Realms + oDIN AI (visual; no new realm backend) ---- */
export const SIDEBAR_EXTRA = [
  { icon: Users, label: 'Communities', path: '/explore' },
  { icon: Gamepad2, label: 'Gaming', path: '/world' },
  { icon: Store, label: 'Marketplace', path: '/explore' },
  { icon: Calendar, label: 'Events', path: '/explore' },
];

export const REALMS = [
  { name: 'Midgard', active: true, sub: 'Thornhaven · current settlement' },
  { name: 'Asgard', locked: true },
  { name: 'Jötunheim', locked: true },
  { name: 'Svartálfheim', locked: true },
  { name: 'Niflheim', locked: true },
];

export const RealmsNav = () => (
  <div className="mt-4 pt-4 border-t border-[hsl(var(--mg-rune)/0.2)]">
    <p className="px-4 mb-2 text-[11px] uppercase tracking-widest text-mg-purple/70 font-bold">My Realms</p>
    <div className="space-y-1">
      {REALMS.map((r) => (
        r.active ? (
          <Link key={r.name} to="/world" className="block px-4 py-2 rounded-xl bg-mg-purple/15 border border-mg-rune/40" data-testid="realm-midgard">
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <span className="w-2 h-2 rounded-full bg-mg-purple mg-glow" /> {r.name}
            </span>
            {r.sub && <span className="block text-[11px] text-muted-foreground ml-4 mt-0.5">{r.sub}</span>}
          </Link>
        ) : (
          <div key={r.name} className="flex items-center gap-2 px-4 py-2 rounded-xl text-muted-foreground/60 cursor-not-allowed" data-testid={`realm-${r.name.toLowerCase()}`}>
            <Lock className="w-3.5 h-3.5" /> <span className="text-sm">{r.name}</span>
            <span className="ml-auto text-[9px] uppercase tracking-wider">Locked</span>
          </div>
        )
      ))}
      <Link to="/world" className="flex items-center gap-2 px-4 py-2 text-sm text-mg-purple hover:underline" data-testid="explore-realms">
        <Compass className="w-4 h-4" /> Explore Realms
      </Link>
    </div>
  </div>
);

export { Bot };
